import { Link } from 'react-router';
import { useT } from '@/i18n/provider';
import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

// Body copy is English-only by design, like the other /docs/* pages; the
// surrounding chrome (nav label, breadcrumb) goes through i18n.

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[0.85em] text-gray-800 dark:bg-gray-800 dark:text-gray-200">
      {children}
    </code>
  );
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: React.ReactNode[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 text-xs align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };

  return (
    <div className={`my-4 rounded-xl border-l-4 px-4 py-3 ${styles[type]}`}>
      <span className="font-semibold text-sm">{labels[type]}:</span>{' '}
      <span className="text-sm">{children}</span>
    </div>
  );
}

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'where', label: 'Where parsing runs', level: 1 },
  { id: 'builder', label: 'Build a parser' },
  { id: 'pick-source', label: '1. Pick a source', level: 1 },
  { id: 'templates', label: '2. Events & templates', level: 1 },
  { id: 'extract', label: '3. Extract & save', level: 1 },
  { id: 'time-level', label: 'Event time & level' },
  { id: 'formats', label: 'Time formats', level: 1 },
  { id: 'editing', label: 'Editing & deleting' },
  { id: 'rules', label: 'Rules & order' },
  { id: 'agent', label: 'Parsing on the agent' },
  { id: 'existing', label: 'Already stored events' },
  { id: 'troubleshooting', label: 'Troubleshooting' },
];

export function ParsersDocsPage() {
  useDocumentMeta({
    title: 'Parsers: LogPulse Docs',
    description: 'Turn raw log events into fields: pick a source, see its events grouped into templates, click what should become a field. One parse layer for agents, API, OTLP and cloud integrations.',
  });

  const t = useT();

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        <div className="mb-8">
          <div className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-1">
            {t('docs.nav.dataProcessing')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">Parsers</h1>
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            A parser turns raw log lines into fields you can search, chart and alert on. You build one from your
            own events: LogPulse groups them into templates, you click the parts that should become fields, and
            the parser applies to that data wherever it comes from.
          </p>
        </div>

        <Section id="overview" title="Overview">
          <P>
            Open <strong>Configure → Data processing → Parsers</strong> in the app. The page has three tabs:
          </P>
          <Table
            headers={['Tab', 'What it is for']}
            rows={[
              ['Build', 'The guided builder: source → events & templates → extract & save. The way in for most parsers.'],
              ['Rules', 'Every parser rule in the order ingest tries them. Write or edit a rule by hand (regex, grok, JSON, logfmt).'],
              ['Built-in (agent)', 'The parsers the LogPulse agent already runs on the host (sshd, sudo, nginx, firewalls, …).'],
            ]}
          />

          <SubSection id="where" title="Where parsing runs">
            <P>
              Parsing happens once, when an event is indexed, in the same step for every source: the LogPulse
              agent, the HTTP API, OTLP, Vector, webhooks, pipelines and cloud integrations. A parser is scoped to
              the <em>data</em> (for example <Code>sourcetype:nginx</Code>), not to how it arrived, so the same
              parser covers an nginx log shipped by an agent and one pushed over the API.
            </P>
            <P>
              Extracted fields are stored with the event and can be used directly in{' '}
              <Link className="text-primary-600 dark:text-primary-400 hover:underline" to="/docs/search/lpql-syntax">LPQL</Link>:{' '}
              <Code>sourcetype=app user=alice | stats count by src_ip</Code>.
            </P>
          </SubSection>
        </Section>

        <Section id="builder" title="Build a parser">
          <SubSection id="pick-source" title="1. Pick a source">
            <P>
              The builder lists what came in over the last 24 hours, by sourcetype, with how much of it is already
              parsed. Click one, or type any scope: <Code>sourcetype</Code>, <Code>index</Code>,{' '}
              <Code>source</Code>, <Code>host</Code> or <Code>service</Code> followed by a value. Wildcards work:{' '}
              <Code>source:app-*</Code>.
            </P>
            <Callout type="tip">
              From an agent group, the <em>Parse</em> step links each of the group&apos;s sourcetypes straight
              into the builder.
            </Callout>
          </SubSection>

          <SubSection id="templates" title="2. Events & templates">
            <P>
              LogPulse reads up to 1,000 recent events of the source and groups them into <strong>templates</strong>:
              lines that share the same structure and differ only in their values. The parts that change, such as
              IP addresses, numbers, timestamps and IDs, are shown as coloured placeholders:
            </P>
            <CodeBlock title="Events">{`2026-10-02T10:00:00Z INFO user alice logged in from 10.0.0.5 via ssh
2026-10-02T10:01:12Z INFO user bob logged in from 10.0.0.6 via ssh`}</CodeBlock>
            <CodeBlock title="Template">{`<TS> INFO user <value> logged in from <IP> via ssh`}</CodeBlock>
            <P>
              Each template says whether a parser already handles it. Templates marked <em>Not parsed</em> are
              what is left to do; a new log format that starts arriving later shows up here as a new, unparsed
              template. Switch to <em>Events</em> to see the raw lines and which template each belongs to. Pick a
              shorter or longer window (last hour, 24 hours, 7 days) to change the sample.
            </P>
          </SubSection>

          <SubSection id="extract" title="3. Extract & save">
            <P>
              Click <em>Extract fields</em> on a template, then click each coloured part that should become a field
              and give it a name (a suggestion is filled in). Parts you don&apos;t click still have to match, so
              the parser stays specific to this template, but they are not stored as fields.
            </P>
            <P>
              The preview on the right runs the parser over the whole sample and shows the extracted fields per
              event and how many events it matches. Prefer to write it yourself? <em>Write a regex instead</em>{' '}
              takes a regular expression with named groups and previews it the same way.
            </P>
            <P>
              <em>Save parser</em> stores it as a parser rule for the source. It applies to events that arrive
              from then on, within about a minute.
            </P>
          </SubSection>
        </Section>

        <Section id="time-level" title="Event time & level">
          <P>
            A parser can say which extracted field is the event&apos;s time and which is its level:
          </P>
          <Table
            headers={['Setting', 'Effect']}
            rows={[
              [<Code key="t">Event time (_time)</Code>, 'The value becomes the event time (_time) used by searches, time ranges and charts. Without it, the event keeps the time it arrived with.'],
              ['Level', 'The value becomes the level column (error, warn, info, …). Common spellings and syslog severities 0–7 are normalised.'],
            ]}
          />
          <P>
            The time the event was indexed is always kept separately as <Code>_indextime</Code>, so a parsed time
            in the past or the future never hides an event: search on what arrived with{' '}
            <Code>_index_earliest=-15m</Code>. See{' '}
            <Link className="text-primary-600 dark:text-primary-400 hover:underline" to="/docs/search/lpql-syntax#index-time">
              index time in LPQL
            </Link>.
          </P>

          <SubSection id="formats" title="Time formats">
            <P>
              Leave the format empty and LogPulse recognises the common shapes: ISO 8601, epoch in seconds,
              milliseconds, microseconds or nanoseconds, access-log time (<Code>02/Oct/2026:10:15:30 +0200</Code>)
              and syslog time (<Code>Oct  2 10:15:30</Code>). For anything else, give a strptime-style format, as
              with Splunk&apos;s <Code>TIME_FORMAT</Code>:
            </P>
            <Table
              headers={['Code', 'Meaning', 'Code', 'Meaning']}
              rows={[
                [<Code key="1">%Y</Code>, 'Year (2026)', <Code key="2">%y</Code>, 'Year, two digits'],
                [<Code key="3">%m</Code>, 'Month (01–12)', <Code key="4">%b / %B</Code>, 'Month name (Oct / October)'],
                [<Code key="5">%d / %e</Code>, 'Day of month', <Code key="6">%j</Code>, 'Day of year'],
                [<Code key="7">%H / %I %p</Code>, 'Hour, 24h / 12h with AM/PM', <Code key="8">%M / %S</Code>, 'Minute / second'],
                [<Code key="9">%f, %3N, %6N, %9N</Code>, 'Fraction of a second', <Code key="10">%z / %Z</Code>, 'Zone (+0200, +02:00, Z / UTC)'],
                [<Code key="11">%s</Code>, 'Epoch seconds', <Code key="12">%T / %F</Code>, '%H:%M:%S / %Y-%m-%d'],
              ]}
            />
            <CodeBlock title="Examples">{`%d-%m-%Y %H:%M:%S,%3N        02-10-2026 10:15:30,123
%Y/%m/%d %I:%M:%S %p          2026/10/02 03:15:30 PM
%a, %d %b %Y %T %z            Fri, 02 Oct 2026 10:15:30 +0000`}</CodeBlock>
            <Callout type="info">
              A time without a zone is read as UTC. A time more than 2 days in the future or 10 years in the past
              is refused: the event keeps its arrival time and gets a <Code>_time_parse_error</Code> field that says
              why.
            </Callout>
          </SubSection>
        </Section>

        <Section id="editing" title="Editing & deleting">
          <P>
            In the builder, a template that one of your parsers handles shows <em>Edit parser</em>. It opens the
            same screen with the fields, time and level filled in; change them, check the preview and save, or
            switch the parser off or delete it. In the <em>Rules</em> tab, parsers made from a template open in
            the builder; other rules open in the rule editor, whose <em>Test</em> button also shows the resulting{' '}
            <Code>_time</Code> and level. Deleting always asks for confirmation.
          </P>
        </Section>

        <Section id="rules" title="Rules & order">
          <P>
            Under the hood a parser is one or more <strong>parser rules</strong> with the same scope, typically one
            per template. For each event, LogPulse tries every rule whose scope matches, highest priority first;
            the first rule that matches the line wins. A rule that does not match hands the line to the next one.
          </P>
          <Table
            headers={['Rule type', 'Use it for']}
            rows={[
              ['Regex', 'Named groups become fields. What the builder generates.'],
              ['Grok', 'Readable patterns such as %{IPORHOST:client} %{NUMBER:status}.'],
              ['JSON', 'Flattens a JSON event into fields (rename them with field mappings).'],
              ['Logfmt / Key=Value', 'key=value pairs. JSON and key=value are also detected automatically for events that no rule is scoped to.'],
            ]}
          />
          <P>
            JSON and key=value events from a source without rules are extracted automatically, so they rarely need
            a parser; add a rule only to rename fields or to choose the time and level field.
          </P>
        </Section>

        <Section id="agent" title="Parsing on the agent">
          <P>
            The LogPulse agent runs built-in parsers on the host for common formats: sshd, sudo and PAM, nginx,
            ufw/iptables, Cisco ASA, UniFi, dnsmasq and more. Their fields arrive with the event. Everything else is
            parsed by the parsers you build here, which apply no matter which agent or integration sent the data.
            See{' '}
            <Link className="text-primary-600 dark:text-primary-400 hover:underline" to="/docs/integrations/agents#parsing">
              Agents: parsing
            </Link>.
          </P>
        </Section>

        <Section id="existing" title="Already stored events">
          <P>
            A new or changed parser does not rewrite events that are already stored. To search older events the
            same way, use <Code>rex</Code> at search time:
          </P>
          <CodeBlock title="LPQL">{`sourcetype=app "logged in" | rex "user (?<user>\\S+) logged in from (?<src_ip>\\S+)" | stats count by user`}</CodeBlock>
        </Section>

        <Section id="troubleshooting" title="Troubleshooting">
          <SubSection id="t-no-events" title="The builder shows no events">
            <P>
              The scope matched nothing in the chosen window. Pick a longer window, check the spelling of the
              sourcetype, and check that your role can read the index the data lands in.
            </P>
          </SubSection>
          <SubSection id="t-not-matching" title="A saved parser does not match new events">
            <P>
              Parse errors are logged per rule to <Code>index=_internal sourcetype=ingest_errors action=parser_error</Code>{' '}
              with a sample event. The usual cause is a log format that changed: the builder then shows the new
              format as a separate, unparsed template.
            </P>
          </SubSection>
          <SubSection id="t-time" title="Events land at the wrong time">
            <P>
              Check the time field in the preview: <em>not read</em> means the format does not match. Without a
              zone the time is read as UTC; include <Code>%z</Code> when the log carries one. Compare{' '}
              <Code>_time</Code> with <Code>_indextime</Code> to see the shift:{' '}
              <Code>| eval lag=_indextime-_time | stats avg(lag) max(lag)</Code>.
            </P>
          </SubSection>
        </Section>
      </div>

      <TableOfContents items={tocItems} />
    </div>
  );
}
